import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik, getIn } from 'formik';
import Grid from '@material-ui/core/Grid';
import { object, string } from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { TP, dateFormats, urlRegex, performancesAggregationTypes, noUrlRegex } from '../../../../constants/consts';
import TextField from '../../../../components/Fields/TextField';
import FormikCheckbox from '../../../../components/Fields/CheckboxField/FormikCheckbox';
import SubmitModalLayout from '../../../../components/SubmitModalLayout';
import { EditModeCardSection } from '../../../../components/EditModeCard';
import CalendarField from '../../../../components/Fields/CalendarField';
import ProductionsLinkBlock from './ProductionsLinkBlock';
import { selectSearchProductionsList } from '../../selectors';
import { cleanSearchProductionsListAction } from '../../actions';
import { selectAggregationsLoading, selectAggregationsOptions } from '../../../App/selectors';
import TitleBlock from '../../../../components/EditComponents/TitleBlock';
import { createDate } from '../../../../utils/date';
import useProductionsMethods from '../../../../utils/hooks/useProductionsMethods';

const useStyles = makeStyles(theme => ({
  labelClassName: {
    '& .MuiFormControlLabel-root': {
      alignItems: 'flex-start',
    },
  },
  checkboxLabel: {
    marginBottom: '0',
    marginTop: '7px',
    '& div': {
      lineHeight: 1,
    },
  },
  urlField: {
    marginBottom: theme.spacing(1),
  },
}));

const formFields = {
  reviewUrl: {
    name: 'reviewUrl',
    label: `edit:${TP}.FN_EDITARTIST_ADD_REVIEW_URL_LABEL`,
    initValue: '',
  },
  title: {
    name: 'title',
    label: `${TP}.FN_REVIEW_TITLE`,
    initValue: '',
  },
  author: {
    name: 'signature',
    label: `${TP}.FN_REVIEW_AUTHOR`,
    initValue: '',
  },
  publisher: {
    name: 'mediaName',
    label: `${TP}.FN_REVIEW_PUBLISHER`,
    initValue: '',
  },
  reviewDate: {
    name: 'reviewedAt',
    label: `edit:${TP}.FN_EDITARTIST_REVIEW_DATE`,
    initValue: '',
  },
  reviewHighlight: {
    name: 'content',
    label: `edit:${TP}.FN_EDITARTIST_REVIEW_HIGHLIGHT`,
    initValue: '',
  },
};
const { reviewUrl, title, author, publisher, reviewDate, reviewHighlight } = formFields;

export function AddReviewDetailsForm({ isOpened, submitFunc, onClose, entity, profile, isLoading }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const formRef = useRef();
  const dispatch = useDispatch();
  const { isProductionRecordCheck, getProductions, searchProductions } = useProductionsMethods(profile?.id, () =>
    setSelectedProduction(null),
  );

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    return () => {
      dispatch(cleanSearchProductionsListAction());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productionsList = useSelector(selectSearchProductionsList());
  const [selectedProduction, setSelectedProduction] = React.useState(entity?.production || null);
  const productionsSearchList = useSelector(selectAggregationsOptions(performancesAggregationTypes.workAgg));
  const loadingFieldName = useSelector(selectAggregationsLoading());

  const getInitValues = React.useCallback(() => {
    const values = {};
    Object.values(formFields).forEach(field => {
      values[field.name] = entity[field.name] || field.initValue;
    });
    const date = get(entity, reviewDate.name);
    values[reviewDate.name] = date ? createDate(date).format(dateFormats.basic) : '';
    values.relatedTo = Boolean(entity?.production?.id);
    return values;
  }, [entity]);

  const submitDetails = () => {
    formRef.current.submitForm();
  };

  const sendData = data => {
    submitFunc({ ...data, production: selectedProduction?.id ? { id: selectedProduction?.id } : null });
  };

  const selectProduction = production => {
    setSelectedProduction(production?.id ? production : null);
  };

  const closeSelectedProduction = () => {
    setSelectedProduction(null);
    getProductions();
  };

  return (
    <SubmitModalLayout
      isOpened={isOpened}
      onClose={onClose}
      title1={t(entity?.id ? `edit:${TP}.FN_EDITARTIST_ADD_DETAILS_TO_REVIEW` : `edit:${TP}.FN_EDITARTIST_ADD_REVIEWS`)}
      updatedDate={entity?.updatedAt || ''}
      submitFunc={submitDetails}
      isLoading={isLoading}
    >
      <Formik
        initialValues={getInitValues()}
        onSubmit={(values, { setSubmitting }) => {
          sendData(values);
          setSubmitting(false);
        }}
        innerRef={formRef}
        className={classes.test}
        validationSchema={ReviewValidationSchema}
        validateOnChange
      >
        {({ values, setFieldValue, errors }) => (
          <Form>
            <EditModeCardSection>
              <Field
                component={TextField}
                label={t(reviewUrl.label)}
                className={classes.urlField}
                name={reviewUrl.name}
              />
              <Grid container spacing={3}>
                <Grid item lg={3} sm={6}>
                  <Field component={TextField} name={title.name} label={t(title.label)} required />
                </Grid>
                <Grid item lg={3} sm={6}>
                  <Field
                    component={TextField}
                    name={author.name}
                    label={t(author.label)}
                    nativeInputProps={{ maxLength: '23%' }}
                    required
                  />
                </Grid>
                <Grid item lg={3} sm={6}>
                  <Field
                    component={TextField}
                    name={publisher.name}
                    label={t(publisher.label)}
                    nativeInputProps={{ maxLength: '23%' }}
                    required
                  />
                </Grid>
                <Grid item lg={3} sm={6}>
                  <CalendarField
                    required
                    name={reviewDate.name}
                    label={t(reviewDate.label)}
                    selectedDay={values[reviewDate.name]}
                    onChange={day =>
                      setFieldValue(reviewDate.name, day ? createDate(day).format(dateFormats.basic) : '')
                    }
                    errorText={t(getIn(errors, reviewDate.name))}
                    hasError={Boolean(getIn(errors, reviewDate.name))}
                  />
                </Grid>
                <Grid item lg={12} sm={12}>
                  <Field
                    label={t(reviewHighlight.label)}
                    name={reviewHighlight.name}
                    component={TextField}
                    inputProps={{
                      multiline: true,
                      rowsMax: 8,
                      rows: 8,
                    }}
                    required
                  />
                </Grid>
              </Grid>
            </EditModeCardSection>
            <EditModeCardSection>
              <Field
                name="relatedTo"
                label={
                  <TitleBlock
                    title={t(`edit:${TP}.FN_EDITARTIST_IS_REVIEW_FROM_PRODUCTION`)}
                    subtitle={t(`edit:${TP}.FN_EDITARTIST_YOU_CAN_ADD_REVIEW_TO_PRODUCTION`)}
                    className={classes.checkboxLabel}
                  />
                }
                classNameRoot={classes.labelClassName}
                component={FormikCheckbox}
                onChange={isProductionRecordCheck}
              />
              {values.relatedTo && (
                <ProductionsLinkBlock
                  selectedProduction={selectedProduction}
                  selectProduction={selectProduction}
                  productionsList={productionsList}
                  getProductions={getProductions}
                  onCloseSelectedProduction={closeSelectedProduction}
                  title={`${TP}.FN_EDITARTIST_SCHEDULE`}
                  searchProductions={searchProductions}
                  searchProductionsList={productionsSearchList}
                  isSearchLoading={loadingFieldName === 'production-search'}
                  onFocus={searchProductions}
                />
              )}
            </EditModeCardSection>
          </Form>
        )}
      </Formik>
    </SubmitModalLayout>
  );
}

AddReviewDetailsForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  submitFunc: PropTypes.func.isRequired,
  isOpened: PropTypes.bool,
  entity: PropTypes.object,
};

AddReviewDetailsForm.defaultProps = {
  isOpened: false,
  entity: {},
};

export default AddReviewDetailsForm;

const ReviewValidationSchema = object().shape({
  [reviewUrl.name]: string().matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`),
  [title.name]: string().required(`${TP}.FN_REQUIRED_FIELD`),
  [author.name]: string().required(`${TP}.FN_REQUIRED_FIELD`),
  [publisher.name]: string().required(`${TP}.FN_REQUIRED_FIELD`),
  [reviewDate.name]: string().required(`${TP}.FN_REQUIRED_FIELD`),
  [reviewHighlight.name]: string()
    .test('min', `${TP}.FN_REQUIRED_FIELD`, val => !!val)
    .matches(noUrlRegex, `${TP}.FN_LINKS_VALIDATION_MESSAGE`), // TODO discuss key for this case
});
